import React, { useEffect, useState } from 'react';
import { cn } from '../lib/utils';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  RotateCw,
  Search,
  RefreshCw,
  ExternalLink,
  History,
  User,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useKYPSearch } from '../lib/kyp-search';
import { useAuth } from '../lib/auth.context';
import { processSearchResults } from '../lib/search-results-processor';
import { HistoricalSearchDialog } from './HistoricalSearchDialog';

interface SearchJob {
  id: number;
  player_id: string;
  player_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  updated_at: string;
  search_timestamp?: string;
  progress_percentage: number;
  player_image_url?: string;
  overall_player_score?: number;
}

interface RecentSearchesProps {
  className?: string;
  searches?: SearchJob[];
  onViewResults?: (searchId: number) => void;
  onRetrySearch?: (playerId: string, playerName: string) => void;
  maxItems?: number;
  title?: string;
  showPlayerImage?: boolean;
  showViewAll?: boolean;
  onViewAllClick?: () => void;
  loading?: boolean;
}

export const RecentSearches: React.FC<RecentSearchesProps> = ({
  className,
  searches: propSearches,
  onViewResults,
  onRetrySearch,
  maxItems = 5,
  title = "Recent Searches",
  showPlayerImage = true,
  showViewAll = false,
  onViewAllClick,
  loading = false
}) => {
  const { getToken } = useAuth();
  const { handlePlayerSearch } = useKYPSearch();
  const [searches, setSearches] = useState<SearchJob[]>(propSearches || []);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  
  // State for historical search dialog
  const [showHistoricalDialog, setShowHistoricalDialog] = useState<boolean>(false);

  // If searches are provided as props, use those
  // Otherwise, fetch them from the API
  useEffect(() => {
    if (propSearches) {
      setSearches(propSearches);
      return;
    }

    const fetchSearchHistory = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = getToken();
        if (!token) {
          setError('Not authenticated');
          setIsLoading(false);
          return;
        }

        // In development, use relative URLs to leverage the Vite proxy
        const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
        const response = await fetch(`${baseUrl}/ai/v1/user/search/history?page=${page}&page_size=${maxItems}&sort_by=created_at&sort_order=desc&include_automated=false`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.status === 'success') {
          // Handle both old and new API response formats
          const searchHistory = data.search_history || data.jobs || [];
          
          // Transform the data to match our SearchJob interface
          const transformedSearches: SearchJob[] = searchHistory.map((job: any) => {
            // Extract progress information
            const progressInfo = job.progress || {};
            
            return {
              id: job.id,
              player_id: job.player_id,
              player_name: job.player_name || `Player ${job.player_id}`,
              status: job.status,
              created_at: job.created_at,
              updated_at: job.updated_at || job.created_at,
              search_timestamp: job.search_timestamp,
              progress_percentage: job.progress_percentage || progressInfo.progress_percentage || 0,
              player_image_url: job.player_image_url,
              overall_player_score: job.overall_player_score
            };
          });
          
          // For completed searches, fetch the overall player score if not already present
          const searchesWithScores = await Promise.all(
            transformedSearches.map(async (search) => {
              // Only fetch scores for completed searches that don't already have a score
              if (search.status === 'completed' && search.overall_player_score === undefined) {
                try {
                  console.log(`Fetching score for search ${search.id}`);
                  const scoreResponse = await fetch(`${baseUrl}/ai/v1/player/search/async/${search.id}/results`, {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    }
                  });
                  
                  if (scoreResponse.ok) {
                    const scoreData = await scoreResponse.json();
                    
                    if (scoreData.status === 'success') {
                      // Try to extract overall_player_score from the response
                      if (scoreData.overall_player_score !== undefined) {
                        console.log(`Found overall_player_score: ${scoreData.overall_player_score}`);
                        return {
                          ...search,
                          overall_player_score: scoreData.overall_player_score
                        };
                      } else {
                        // If overall_player_score is not directly in the response, try to process the data
                        try {
                          const { status, message, player_id, ...playerDataFields } = scoreData;
                          const processed = processSearchResults(playerDataFields);
                          
                          if (processed.overall_player_score !== undefined) {
                            console.log(`Extracted overall_player_score from processed data: ${processed.overall_player_score}`);
                            return {
                              ...search,
                              overall_player_score: processed.overall_player_score
                            };
                          }
                        } catch (processingError) {
                          console.error(`Error processing score data for search ${search.id}:`, processingError);
                        }
                      }
                    }
                  }
                } catch (error) {
                  console.error(`Error fetching score for search ${search.id}:`, error);
                }
              }
              return search;
            })
          );
          
          console.log("Search history with scores:", searchesWithScores);
          setSearches(searchesWithScores);
          
          // Set pagination data if available
          if (data.pagination) {
            setTotalPages(data.pagination.total_pages || 1);
            setTotalItems(data.pagination.total_items || 0);
          }
        } else {
          setSearches([]);
          setTotalPages(1);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching search history:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch search history');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSearchHistory();
  }, [getToken, maxItems, propSearches, page]);

  // Default handlers if not provided
  const defaultViewResults = async (searchId: number) => {
    // Find the search job
    const search = searches.find(s => s.id === searchId);
    if (search) {
      // Just use the standard player search function - the fix is in the KYP context
      handlePlayerSearch(search.player_id, search.player_name);
    }
  };

  const defaultRetrySearch = (playerId: string, playerName: string) => {
    // Use the KYP search context to retry the search
    handlePlayerSearch(playerId, playerName);
  };

  // We'll filter the searches later in the component

  if (isLoading) {
    return (
      <div className={cn(
        "bg-[#121212] rounded-lg shadow-lg border border-gray-700 p-5",
        className
      )}>
        <div className="flex items-center justify-center py-6 text-[#CECFD2]">
          <RotateCw className="h-6 w-6 animate-spin mr-2" />
          <p>Loading recent searches...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "bg-[#121212] rounded-lg shadow-lg border border-red-800 p-5",
        className
      )}>
        <div className="flex items-center justify-center py-6 text-red-400">
          <AlertCircle className="h-6 w-6 mr-2" />
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  // Filter searches to only show successful or in-progress searches
  const filteredSearches = searches.filter(search => 
    search.status === 'completed' || search.status === 'in_progress' || search.status === 'pending'
  );
  
  if (filteredSearches.length === 0) {
    return (
      <div className={cn(
        "bg-[#121212] rounded-lg shadow-lg border border-gray-700 p-5",
        className
      )}>
        <div className="flex flex-col items-center justify-center py-6 text-[#CECFD2]">
          <History className="h-10 w-10 mb-3 opacity-50" />
          <p>No successful searches found</p>
        </div>
      </div>
    );
  }

  // Handle view all click
  const handleViewAllClick = () => {
    if (onViewAllClick) {
      onViewAllClick();
    } else {
      setShowHistoricalDialog(true);
    }
  };
  
  return (
    <>
      <div className={cn(
        "bg-[#121212] rounded-lg shadow-lg border border-gray-700 p-5",
        className
      )}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-[#CECFD2]">{title}</h3>
          
          {showViewAll && (
            <button
              onClick={handleViewAllClick}
              className="text-sm text-[#4E46FF] hover:text-[#3d37cc] font-medium flex items-center gap-1"
            >
              <span>View all</span>
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
        
        <div className="space-y-3">
          {filteredSearches.map((search) => (
            <div 
              key={search.id}
              className={cn(
                "flex items-center justify-between p-3 rounded-md border",
                search.status === 'completed' ? "border-green-800 bg-[#26222E]" :
                search.status === 'in_progress' ? "border-blue-800 bg-[#26222E]" :
                search.status === 'failed' ? "border-red-800 bg-[#26222E]" :
                search.status === 'cancelled' ? "border-amber-800 bg-[#26222E]" :
                "border-gray-700 bg-[#26222E]"
              )}
            >
              <div className="flex items-center gap-3">
                {/* Player image */}
                {showPlayerImage && (
                  <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 bg-[#26222E]">
                    <img 
                      src={search.player_image_url || '/footballer.png'} 
                      alt={search.player_name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
                
                {/* Overall Player Score Badge - Only for completed searches */}
                {search.status === 'completed' && (
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold",
                    search.overall_player_score !== undefined ? (
                      search.overall_player_score >= 7 ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" :
                      search.overall_player_score >= 4 ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100" :
                      "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                    ) : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                  )}>
                    {search.overall_player_score !== undefined ? search.overall_player_score : "?"}
                  </div>
                )}
                
                {/* Status icon - only show for non-completed statuses */}
                {search.status === 'in_progress' ? (
                  <RotateCw className="h-5 w-5 text-blue-500 animate-spin" />
                ) : search.status === 'failed' ? (
                  <XCircle className="h-5 w-5 text-red-500" />
                ) : search.status === 'cancelled' ? (
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                ) : search.status === 'pending' ? (
                  <Clock className="h-5 w-5 text-gray-500" />
                ) : null}
                
                {/* Player name */}
                <div>
                  <p className="font-medium text-[#CECFD2]">
                    {search.player_name || `Player ${search.player_id}`}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#CECFD2]">
                    <Clock className="h-3 w-3" />
                    <span>{formatDistanceToNow(new Date(search.created_at), { addSuffix: true })}</span>
                    
                    {/* Progress for in-progress searches */}
                    {search.status === 'in_progress' && (
                      <>
                        <span className="mx-1">•</span>
                        <span>{search.progress_percentage}% complete</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex items-center gap-2">
                {search.status === 'completed' && (
                  <button
                    onClick={() => onViewResults ? onViewResults(search.id) : defaultViewResults(search.id)}
                    className="text-xs text-[#4E46FF] hover:text-[#3d37cc] font-medium flex items-center gap-1 px-2 py-1 rounded-md hover:bg-[#26222E] transition-colors"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>View</span>
                  </button>
                )}
                
                {(search.status === 'failed' || search.status === 'cancelled') && (
                  <button
                    onClick={() => onRetrySearch ? onRetrySearch(search.player_id, search.player_name) : defaultRetrySearch(search.player_id, search.player_name)}
                    className="text-xs text-[#CECFD2] hover:text-white font-medium flex items-center gap-1 px-2 py-1 rounded-md hover:bg-[#26222E] transition-colors"
                  >
                    <RefreshCw className="h-3 w-3" />
                    <span>Retry</span>
                  </button>
                )}
              </div>
            </div>
          ))}
          
          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className={cn(
                  "flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md",
                  page === 0 
                    ? "text-gray-600 cursor-not-allowed" 
                    : "text-[#4E46FF] hover:text-[#3d37cc] hover:bg-[#26222E]"
                )}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </button>
              
              <span className="text-sm text-[#CECFD2]">
                Page {page + 1} of {totalPages}
              </span>
              
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className={cn(
                  "flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md",
                  page >= totalPages - 1 
                    ? "text-gray-600 cursor-not-allowed" 
                    : "text-[#4E46FF] hover:text-[#3d37cc] hover:bg-[#26222E]"
                )}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Historical Search Dialog */}
      {showHistoricalDialog && (
        <HistoricalSearchDialog 
          onClose={() => setShowHistoricalDialog(false)} 
        />
      )}
    </>
  );
};